import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp, faSearch } from '@fortawesome/free-solid-svg-icons';
import './FAQ.css';

const FAQ = () => {
  const [activeCategory, setActiveCategory] = useState('general');
  const [expandedItems, setExpandedItems] = useState({});
  const [searchQuery, setSearchQuery] = useState('');

  const faqCategories = [
    { id: 'general', name: 'General' },
    { id: 'appointments', name: 'Appointments' },
    { id: 'billing', name: 'Billing & Insurance' },
    { id: 'services', name: 'Services' },
    { id: 'prescriptions', name: 'Prescriptions & Medication' },
    { id: 'covid', name: 'COVID-19 Information' }
  ];

  const faqItems = [
    // General
    {
      id: 1,
      category: 'general',
      question: 'What are your opening hours?',
      answer: 'Our clinic is open Monday to Friday from 8:00 AM to 6:00 PM, and Saturday from 9:00 AM to 1:00 PM. We are closed on Sundays and public holidays. For after-hours medical assistance, please call the National Home Doctor Service on 13 SICK (13 7425).'
    },
    {
      id: 2,
      category: 'general',
      question: 'Do I need to be registered with your clinic to see a doctor?',
      answer: 'Yes, we ask that all patients register with our clinic. This can be done during your first visit or online through our website. Registration helps us maintain accurate records and provide continuity of care. Please arrive 15 minutes before your first appointment to complete the necessary paperwork, or download the registration forms from our website and bring them completed.'
    },
    {
      id: 3,
      category: 'general',
      question: 'What should I bring to my first appointment?',
      answer: 'For your first appointment, please bring your Medicare card, any healthcare cards (e.g., pension, healthcare, or DVA cards), a list of current medications, and any relevant medical records or test results. If you\'re transferring from another practice, it\'s helpful to have your previous doctor\'s details so we can request your medical history.'
    },
    {
      id: 4,
      category: 'general',
      question: 'Do you have parking facilities?',
      answer: 'Yes, we have free parking available for patients directly in front of our clinic. There is also additional street parking nearby. For patients with mobility issues, we have designated accessible parking spaces close to the entrance.'
    },
    
    // Appointments
    {
      id: 5,
      category: 'appointments',
      question: 'How do I make an appointment?',
      answer: 'You can make an appointment by calling our reception at (02) 4997 1234 during our opening hours, using our online booking system on our website, or by visiting our practice in person. For urgent matters, please call us directly rather than using the online booking system.'
    },
    {
      id: 6,
      category: 'appointments',
      question: 'What is your cancellation policy?',
      answer: 'We request that you provide at least 24 hours\' notice if you need to cancel or reschedule your appointment. This allows us to offer the time slot to another patient. Repeated late cancellations or no-shows may incur a fee.'
    },
    {
      id: 7,
      category: 'appointments',
      question: 'How long are standard appointments?',
      answer: 'Standard appointments are 15 minutes. If you believe you may need more time with the doctor (for example, for multiple or complex issues, mental health concerns, or procedures), please request a longer appointment when booking.'
    },
    {
      id: 8,
      category: 'appointments',
      question: 'Do you offer telehealth consultations?',
      answer: 'Yes, we offer telehealth consultations for eligible patients. These can be conducted via phone or video call. Please contact our reception to determine if your concern is suitable for a telehealth appointment and to book a session.'
    },
    
    // Billing & Insurance
    {
      id: 9,
      category: 'billing',
      question: 'What are your fees?',
      answer: 'Our standard consultation fee is $85 for a regular appointment. Medicare rebates are available, which typically return $39.75 to eligible patients. Longer appointments and procedures incur additional fees. Concession card holders, children under 16, and veterans may be eligible for bulk billing. Please check our Fee Information page for a detailed list of our services and associated costs.'
    },
    {
      id: 10,
      category: 'billing',
      question: 'Do you bulk bill?',
      answer: 'We offer bulk billing for eligible patients including children under 16 years, pensioners, healthcare card holders, and DVA card holders. All other patients will need to pay the gap fee. Please inquire at reception about your eligibility for bulk billing.'
    },
    {
      id: 11,
      category: 'billing',
      question: 'How do I pay for my appointment?',
      answer: 'We accept payment via EFTPOS, credit card (Visa and Mastercard), cash, and direct bank transfer. Payment is required at the time of consultation. For Medicare-eligible services, we can process your rebate immediately if you have registered your bank details with Medicare.'
    },
    {
      id: 12,
      category: 'billing',
      question: 'Are your services covered by private health insurance?',
      answer: 'General practice consultations are covered by Medicare, not private health insurance. However, some additional services provided at our clinic may be eligible for private health insurance rebates. We recommend checking with your health fund regarding specific services.'
    },
    
    // Services
    {
      id: 13,
      category: 'services',
      question: 'What services do you offer?',
      answer: 'We offer a comprehensive range of medical services including general consultations, preventive health checks, chronic disease management, women\'s and men\'s health services, children\'s health, immunizations, minor surgical procedures, mental health services, and travel medicine. Please visit our Services page for a detailed list of all available services.'
    },
    {
      id: 14,
      category: 'services',
      question: 'Do you provide specialist referrals?',
      answer: 'Yes, our doctors can provide referrals to specialists when necessary. During your consultation, your doctor will assess your condition and, if appropriate, provide a referral to a relevant specialist. Our experienced doctors have professional networks with specialists throughout the region and can help guide you to the most suitable specialist for your needs.'
    },
    {
      id: 15,
      category: 'services',
      question: 'Do you offer mental health services?',
      answer: 'Yes, our doctors are trained to provide mental health support and can create Mental Health Treatment Plans for eligible patients. Through a Mental Health Treatment Plan, you may be eligible for Medicare-subsidized sessions with psychologists and other mental health professionals. We also have a visiting psychologist at our practice on certain days of the week.'
    },
    {
      id: 16,
      category: 'services',
      question: 'Can I get a medical certificate from your clinic?',
      answer: 'Yes, our doctors can provide medical certificates for illness, injury, or other medical conditions that affect your ability to work or attend school. You will need to book an appointment and be assessed by a doctor to receive a medical certificate. Backdated certificates are generally not provided.'
    },
    
    // Prescriptions & Medication
    {
      id: 17,
      category: 'prescriptions',
      question: 'How do I request a repeat prescription?',
      answer: 'For repeat prescriptions of regular medications, you will generally need to see the doctor. In some cases, for long-term medications where you have had a recent review, we may be able to provide a repeat prescription without an appointment, but this is at the doctor\'s discretion and a fee may apply. Please call reception to discuss your specific situation.'
    },
    {
      id: 18,
      category: 'prescriptions',
      question: 'Can I get my prescription filled at your clinic?',
      answer: 'No, we do not have an on-site pharmacy. However, there is a pharmacy located within walking distance of our clinic that can fill your prescriptions. Our reception staff can provide directions if needed.'
    },
    {
      id: 19,
      category: 'prescriptions',
      question: 'How long are prescriptions valid?',
      answer: 'Standard prescriptions are typically valid for 12 months from the date of issue, though this can vary depending on the medication. Prescriptions for certain medications, such as controlled substances, may have shorter validity periods. Your doctor will advise you of any specific restrictions for your medications.'
    },
    {
      id: 20,
      category: 'prescriptions',
      question: 'Do you provide electronic prescriptions?',
      answer: 'Yes, we offer electronic prescriptions which can be sent directly to your preferred pharmacy or to your mobile phone as a token. Please let your doctor know during your consultation if you would prefer an electronic prescription.'
    },
    
    // COVID-19 Information
    {
      id: 21,
      category: 'covid',
      question: 'Do you offer COVID-19 vaccinations?',
      answer: 'Yes, we provide COVID-19 vaccinations in accordance with the current Australian government guidelines. Please call our reception or check our website for the latest information on vaccine availability and eligibility.'
    },
    {
      id: 22,
      category: 'covid',
      question: 'What COVID-19 safety measures do you have in place?',
      answer: 'We maintain strict infection control protocols including regular sanitization of surfaces, social distancing in waiting areas, provision of hand sanitizer, and screening of patients for COVID-19 symptoms. Our staff follow all current health department guidelines to ensure the safety of our patients and team.'
    },
    {
      id: 23,
      category: 'covid',
      question: 'Can I get a COVID-19 test at your clinic?',
      answer: 'Yes, we offer COVID-19 testing for symptomatic patients. Please call ahead if you have symptoms, as we may ask you to wait in your car rather than the waiting room. We can also provide information about other testing locations in the area if our appointment times are full.'
    },
    {
      id: 24,
      category: 'covid',
      question: 'What should I do if I have COVID-19 symptoms?',
      answer: 'If you have COVID-19 symptoms (fever, cough, sore throat, shortness of breath, runny nose, loss of taste or smell), please call us before coming to the clinic. We can provide advice on whether you should come in for testing, arrange a telehealth consultation, or direct you to appropriate testing facilities. Please do not attend the clinic without calling first if you have symptoms.'
    }
  ];

  const handleCategoryChange = (categoryId) => {
    setActiveCategory(categoryId);
    setExpandedItems({});
  };

  const toggleItem = (itemId) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setActiveCategory('all');
  };

  const filteredFaqItems = faqItems.filter(item => {
    if (searchQuery) {
      return (
        item.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.answer.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    return activeCategory === 'all' || item.category === activeCategory;
  });

  return (
    <div className="faq-page">
      {/* Page Header */}
      <div className="page-header">
        <div className="container">
          <h1>Frequently Asked Questions</h1>
          <p>Find answers to common questions about our services and policies</p>
        </div>
      </div>

      {/* FAQ Content */}
      <section className="section faq-content">
        <div className="container">
          {/* Search Bar */}
          <div className="faq-search">
            <div className="search-container">
              <input
                type="text"
                placeholder="Search for questions or keywords..."
                value={searchQuery}
                onChange={handleSearchChange}
              />
              <FontAwesomeIcon icon={faSearch} className="search-icon" />
            </div>
          </div>

          {/* FAQ Categories */}
          <div className="faq-categories">
            <button
              className={`category-btn ${activeCategory === 'all' ? 'active' : ''}`}
              onClick={() => handleCategoryChange('all')}
            >
              All
            </button>
            {faqCategories.map(category => (
              <button
                key={category.id}
                className={`category-btn ${activeCategory === category.id ? 'active' : ''}`}
                onClick={() => handleCategoryChange(category.id)}
              >
                {category.name}
              </button>
            ))}
          </div>

          {/* FAQ Items */}
          <div className="faq-items">
            {filteredFaqItems.length > 0 ? (
              filteredFaqItems.map(item => (
                <div key={item.id} className="faq-item">
                  <div 
                    className={`faq-question ${expandedItems[item.id] ? 'active' : ''}`}
                    onClick={() => toggleItem(item.id)}
                  >
                    <h3>{item.question}</h3>
                    <FontAwesomeIcon 
                      icon={expandedItems[item.id] ? faChevronUp : faChevronDown} 
                      className="toggle-icon"
                    />
                  </div>
                  {expandedItems[item.id] && (
                    <div className="faq-answer">
                      <p>{item.answer}</p>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="no-results">
                <p>No results found for "{searchQuery}". Please try another search term.</p>
              </div>
            )}
          </div>

          {/* Still Have Questions */}
          <div className="still-have-questions">
            <h2>Still Have Questions?</h2>
            <p>If you couldn't find the answer to your question, please don't hesitate to contact us.</p>
            <div className="contact-options">
              <div className="contact-option">
                <h3>Call Us</h3>
                <p>(02) 4997 1234</p>
              </div>
              <div className="contact-option">
                <h3>Email Us</h3>
                <p>info@teagardensgp.com.au</p>
              </div>
              <div className="contact-option">
                <h3>Visit Us</h3>
                <p>123 Tea Gardens Road, Tea Gardens, NSW 2324</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQ;
